import * as fabric from "@/models/products/fabric";
import axios, { AxiosResponse } from "axios";
import { APIError, NotFoundError } from "./error_service";

axios.defaults.headers.apikey = process.env.VUE_APP_API_KEY;

/**
 * Creates a new Fabric Service

 * @example
 * const fabricService = new FabricService();
 * fabricService.getFabrics();
 */

export default class FabricService {
  /**
   * Fetch filtered fabrics
   * @param {object} optionsObject optional filters, grade & brand
   * @returns {FabricSwatch[]} - response array of fabric swatches
   */
  public async getFabrics(optionsObject?: {
    grade?: fabric.FabricGrade[];
    brand?: fabric.FabricMfr;
    isAdmin?: boolean;
    isFringe?: boolean;
    isBinding?: boolean;
  }): Promise<fabric.FabricSwatch[]> {
    let fabrics = [];
    let url = process.env.VUE_APP_API_NODE_URL + "/fabrics";
    if (optionsObject) {
      let counter = 0;
      let char = "?";
      if (optionsObject.grade) {
        for (const grade of optionsObject.grade) {
          counter++;
          if (counter > 1) {
            char = "&";
          }
          url += char + "grade=" + encodeURIComponent(String(grade));
        }
      }
      if (optionsObject.brand) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "brand=" + encodeURIComponent(String(optionsObject.brand));
      }
      if (optionsObject.isAdmin) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "admin=" + optionsObject.isAdmin;
      }
      if (optionsObject.isFringe) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "fringe=" + optionsObject.isFringe;
      }
      if (optionsObject.isBinding) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "binding=" + optionsObject.isBinding;
      }
    }
    try {
      const options = {
        withCredentials: true
      };
      const res: AxiosResponse = await axios.get(url, options);
      fabrics = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return fabrics;
        // throw new NotFoundError(
        //   "No fabrics found. Try a different search.",
        //   err.response.status
        // );
      } else {
        throw new APIError(
          "Unable to get fabric data. Please try again.",
          err.response.status
        );
      }
    }
    if (!optionsObject?.isFringe && !optionsObject?.isBinding) {
      fabrics = fabrics.filter(
        (f: fabric.FabricSwatch) => f.fabric_type !== "polycotton"
      );
    }
    return fabrics;
  }

  /**
   * Fetch all fabrics without filters
   * @param {object} optionsObject optional context — pass isFringe/isBinding
   *        to retain polycotton fabrics for those component types.
   * @returns {FabricSwatch[]} - response array of all fabric swatches
   */
  public async getAllFabrics(optionsObject?: {
    isAdmin?: boolean;
    isFringe?: boolean;
    isBinding?: boolean;
  }): Promise<fabric.FabricSwatch[]> {
    let fabrics;
    let url = process.env.VUE_APP_API_NODE_URL + "/fabrics";
    if (optionsObject?.isAdmin) {
      url += "?admin=" + optionsObject.isAdmin;
    }
    try {
      const options = {
        withCredentials: true
      };
      const res: AxiosResponse = await axios.get(url, options);
      fabrics = res.data.data;
    } catch (err) {
      throw new APIError(
        "Unable to get all fabric data. Please try again.",
        err.response?.status || 500
      );
    }
    if (!optionsObject?.isFringe && !optionsObject?.isBinding) {
      fabrics = fabrics.filter(
        (f: fabric.FabricSwatch) => f.fabric_type !== "polycotton"
      );
    }
    return fabrics;
  }

  /**
   * Fetch a single fabric by mfr_code (or alt_mfr_code). The API may return
   * multiple matches (duplicate mfr_codes across brand collections); this
   * method returns only the first match. Callers that need to disambiguate
   * should use `getSwatchesByCode()` below and pick from the array.
   *
   * @param {number | string} mfr_code - The manufacturer code (matched against
   *        mfr_code and alt_mfr_code server-side).
   * @returns {FabricSwatch} - first fabric returned for the given code
   */
  public async getSingleFabric(
    mfr_code: number | string
  ): Promise<fabric.FabricSwatch> {
    const fabrics = await this.getSwatchesByCode(mfr_code);
    return fabrics[0];
  }

  /**
   * Fetch all fabrics matching a mfr_code (or alt_mfr_code). The API returns
   * an array because a single mfr_code can now match multiple fabrics across
   * brand collections (e.g. SENS "D-020" exists in Coquette, Finesse, Enea).
   *
   * @param {number | string} mfr_code
   * @param {object} filters - optional server-side disambiguation filters
   * @returns {FabricSwatch[]}
   */
  public async getSwatchesByCode(
    mfr_code: number | string,
    filters?: { group?: string; brand_collection?: string; grade?: string }
  ): Promise<fabric.FabricSwatch[]> {
    // Encode the code — mfr_codes can contain "/", "#", spaces and other
    // characters that would otherwise corrupt the path segment.
    let url =
      process.env.VUE_APP_API_NODE_URL +
      "/fabrics/" +
      encodeURIComponent(String(mfr_code));
    const params: string[] = [];
    // `group` is the new disambiguator (filters by name.group server-side).
    if (filters?.group) params.push(`group=${encodeURIComponent(filters.group)}`);
    // `brand_collection` retained for backwards compat with any caller still
    // using the legacy disambiguator.
    if (filters?.brand_collection) params.push(`brand_collection=${encodeURIComponent(filters.brand_collection)}`);
    if (filters?.grade) params.push(`grade=${encodeURIComponent(filters.grade)}`);
    if (params.length) url += "?" + params.join("&");
    try {
      const options = { withCredentials: true };
      const res: AxiosResponse = await axios.get(url, options);
      const data = res.data.data;
      return Array.isArray(data) ? data : [data];
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Fabric swatch not found.",
          err.response.status
        );
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
  }
}
